import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import qs from 'querystringify';
import { AnalyticsSpinner } from '../../../components/common/Spinner/AnalyticsSpinner';
import PageLayout from '../../../components/common/PageLayout/PageLayout';
import { ShopifySelectRetailer } from '../../../components/blocks/ShopifySelectRetailer/ShopifySelectRetailer';
import { validateAndGetGrantPageUrl, redirectToShopify } from '../../../store/actions/shopifyAppInstall';
import { signInFlow } from '../../../store/actions/auth';
import { PREVIOUS_LOCATION } from '../../../constants/localStorageKeys';

export const ShopifyAppType = {
  RETAILER: 'retailer',
  MARKETPLACE: 'marketplace',
};

export function ShopifyAuthContainer({ shopifyAppType }) {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.role);
  const {
    redirectUrl,
    loading: { redirectUrl: loading },
    updatingScopesOnly,
  } = useSelector((state) => state.shopify);

  const [retailerId, setRetailerId] = useState(0);
  const location = useLocation();

  const noRetailers = useMemo(() => user?.retailers?.length === 0, [user]);
  const singleRetailer = useMemo(() => user?.retailers?.length === 1, [user]);

  const shouldSelectRetailer = useMemo(
    () => !retailerId && !singleRetailer,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.retailers, retailerId],
  );

  useEffect(() => {
    if (!user) {
      const queryParams = new URLSearchParams(location.search);
      queryParams.append('app_type', shopifyAppType);
      const authUrl = shopifyAppType === ShopifyAppType.RETAILER ? 'auth' : 'marketplace-auth';
      sessionStorage.setItem(PREVIOUS_LOCATION, `/shopify/retailers/${authUrl}?${queryParams}`);
      dispatch(signInFlow());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (redirectUrl || loading || shouldSelectRetailer) {
      return;
    }

    const queryParams = qs.parse(location.search);
    queryParams.app_type = shopifyAppType;
    if (retailerId) {
      queryParams.retailer_id = retailerId;
    }

    dispatch(validateAndGetGrantPageUrl(queryParams));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, shouldSelectRetailer]);

  useEffect(() => {
    if (redirectUrl) {
      redirectToShopify({
        url: redirectUrl,
        shouldOpenInNewWindow: updatingScopesOnly,
      });
    }
  }, [redirectUrl, updatingScopesOnly]);

  return (
    <PageLayout>
      <div style={{ marginTop: '3em', textAlign: 'center' }}>
        <h2>Connecting your Shopify Account...</h2>
        {noRetailers ? (
          <h3 style={{ marginTop: '2em', lineHeight: 1.5 }}>
            You don't have any retailers connected to this account.
            <br />
            If there are any questions, please contact{' '}
            <a style={{ color: 'black' }} href="mailto:support@cym.bio">
              support@cym.bio
            </a>
            .
          </h3>
        ) : shouldSelectRetailer ? (
          <ShopifySelectRetailer onContinue={setRetailerId} />
        ) : loading ? (
          <AnalyticsSpinner fullScreen />
        ) : (
          <h3 style={{ marginTop: '2em', lineHeight: 1.5 }}>
            If you haven't seen a popup from Shopify, please check your browser settings.
          </h3>
        )}
      </div>
    </PageLayout>
  );
}
